import { Check } from "@mui/icons-material";
import { Box, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import ContactUsButton from "app/frontend/components/buttons/ContactUsButton";
import { useValidatePlug } from "app/frontend/components/eligibility/accordions/project_size/hooks/useValidatePlug";
import { getKwFromPlug } from "app/frontend/components/eligibility/utils";
import GreenH2 from "app/frontend/components/GreenH2";
import LinkButton from "app/frontend/components/LinkButton";
import { EIncentiveName, EProjectTypes } from "app/frontend/constants/enums";
import { CON_EDISON_MHD_APPLICATION_URL } from "app/frontend/constants/external_urls";
import { VALID_VOUCHERS } from "app/frontend/constants/mhd";
import { ROUTES } from "app/frontend/constants/routes";
import { useParamsProjectType } from "app/frontend/hooks/project/useParamsProjectType";
import { useProject } from "app/frontend/hooks/project/useProject";
import { useIsMHD } from "app/frontend/hooks/useIsMHD";
import { useProjectParams } from "app/frontend/hooks/usePropsParams";
import { asDollars, asPercent } from "app/frontend/utils/currency";
import { Fragment, useMemo } from "react";

const DEFAULT_DOLLARS_PER_KW_CUSTOMER_SIDE_COSTS = 490.38;
const MAX_DOLLARS_PER_KW_CUSTOMER_SIDE_COSTS = 21_000_000; // amount in ConEd budget

export const useProjectConfirmationMHD = () => {
    const projectType = useParamsProjectType();
    const isMHD = useIsMHD();

    const project = useProject();
    const projectParams = useProjectParams();

    const incentiveGroup = project?.incentive_groups?.first();
    const dollars = incentiveGroup?.incentives?.dollars ?? [];
    const dollarsPerKwCustomerSideCosts =
        dollars.find((incentive) => incentive.incentive_type === EIncentiveName.BASELINE)?.amount_per_unit ||
        DEFAULT_DOLLARS_PER_KW_CUSTOMER_SIDE_COSTS;
    const percents = incentiveGroup?.incentives?.percent ?? [];
    const dacPercent = percents.find((incentive) => incentive.incentive_type === EIncentiveName.DAC)?.total ?? 0;
    const publicPercent =
        percents.find((incentive) => incentive.incentive_type === EIncentiveName.PUBLICLY_ACCESSIBLE)?.total ?? 0;

    const { plugs } = projectParams;
    const isValidPlug = useValidatePlug();
    const countKw = plugs?.reduce((sum, plug) => sum + getKwFromPlug(plug), 0) ?? 0;
    const customerSideCosts = publicPercent && countKw ? dollarsPerKwCustomerSideCosts * Number(countKw) : 0;

    const isShowCustomerSideCostsInDollars = publicPercent && (plugs?.length || 0) > 0 && plugs?.every(isValidPlug);
    const csc = isShowCustomerSideCostsInDollars
        ? asDollars(
              customerSideCosts > MAX_DOLLARS_PER_KW_CUSTOMER_SIDE_COSTS
                  ? MAX_DOLLARS_PER_KW_CUSTOMER_SIDE_COSTS
                  : customerSideCosts,
          )
        : `${asPercent(publicPercent)}`;

    const h2 = useMemo(() => {
        if (projectType !== EProjectTypes.MHD) return;

        if (isShowCustomerSideCostsInDollars && dacPercent) {
            return (
                <Typography variant="h2" component={"div"}>
                    You may be eligible for up to <GreenH2>{csc}</GreenH2> in Customer-Side Incentives and up to{" "}
                    <GreenH2>{asPercent(dacPercent)}</GreenH2> of Utility-Side Costs.
                </Typography>
            );
        } else if (dacPercent && publicPercent) {
            return (
                <Typography variant="h2" component={"div"}>
                    You May Be Eligible For Up To <GreenH2>{asPercent(publicPercent)}</GreenH2> in Customer-Side
                    Incentives And Up To <GreenH2>{asPercent(dacPercent)}</GreenH2> of Utility-Side Costs.
                </Typography>
            );
        } else if (dacPercent) {
            return (
                <Typography variant="h2" component={"div"}>
                    You may be eligible for up to <GreenH2>{asPercent(dacPercent)}</GreenH2> of Utility-Side Costs.
                </Typography>
            );
        } else {
            return "Your Project May Qualify for Medium- Heavy-Duty Pilot Incentives. Let's Confirm Your Eligibility.";
        }
    }, [csc, dacPercent, isShowCustomerSideCostsInDollars, projectType, publicPercent]);

    const subheader = useMemo(() => {
        if (projectType !== EProjectTypes.MHD) return;

        if (isShowCustomerSideCostsInDollars && dacPercent) {
            return (
                <Typography variant="h4" component={"div"}>
                    You may be eligible for up to {csc} customer-side and up to {asPercent(dacPercent)} of Utility-Side
                    Costs because:
                </Typography>
            );
        } else if (dacPercent && publicPercent) {
            return (
                <Typography variant="h4" component={"div"}>
                    You may be eligible for up to {asPercent(publicPercent)} customer-side incentives and up to{" "}
                    {asPercent(dacPercent)} of Utility-Side Costs because:
                </Typography>
            );
        } else if (dacPercent) {
            return (
                <Typography variant="h4" component={"div"}>
                    You may be eligible for up to {asPercent(dacPercent)} of utility-side costs because:
                </Typography>
            );
        } else {
            return "How you could qualify:";
        }
    }, [csc, dacPercent, isShowCustomerSideCostsInDollars, projectType, publicPercent]);

    const bullets = useMemo(() => {
        if (projectType !== EProjectTypes.MHD) return [];

        const _bullets = [];
        if (dacPercent || publicPercent) {
            if (project.is_publicly_available) {
                _bullets.push("Your location is publicly accessible.");
            }
            if (!project.is_publicly_available && projectParams?.incentive_names?.includes("dac")) {
                _bullets.push("Your project is in a Disadvantaged Community (DAC).");
            }
            if (incentiveGroup.vouchers && VALID_VOUCHERS.some((v) => incentiveGroup.vouchers.includes(v))) {
                _bullets.push("You're enrolled in a Voucher Incentive Program.");
            }
        } else {
            if (!project.is_publicly_available) {
                _bullets.push("Ensure your site is publicly accessible.");
            }
            if (!projectParams?.incentive_names?.includes("dac")) {
                _bullets.push("Ensure your site is in a Disadvantaged Community (DAC).");
            }
            if (!VALID_VOUCHERS.some((v) => incentiveGroup?.vouchers?.includes(v))) {
                _bullets.push("Participate in a Voucher Incentive Program.");
            }
        }
        return _bullets;
    }, [
        projectType,
        dacPercent,
        publicPercent,
        project.is_publicly_available,
        projectParams?.incentive_names,
        incentiveGroup.vouchers,
    ]);

    const nextSteps = useMemo(() => {
        if (projectType !== EProjectTypes.MHD) return;

        if (publicPercent || dacPercent) {
            return (
                <Stack direction={"column"} gap={1} sx={{ border: "1px solid", borderColor: "secondary.dark", p: 3 }}>
                    <Typography component={"div"} fontWeight={700}>
                        Next Steps:
                    </Typography>
                    <Typography component={"p"}>
                        <b>Step 1:</b>&nbsp;Schedule a call with with a Con Edison advisor.
                    </Typography>
                    <Typography component={"p"}>
                        <b>Step 2:</b>&nbsp;Confirm capacity on site through a{" "}
                        <LinkButton
                            button={{
                                sx: {
                                    textTransform: "none",
                                    fontSize: "inherit",
                                    textDecoration: "underline",
                                    verticalAlign: "unset",
                                },
                            }}
                            href="https://www.coned.com/en/our-energy-future/electric-vehicles/advisory-services"
                        >
                            complimentary site assessment
                        </LinkButton>{" "}
                        and finalize incentive eligibility.
                    </Typography>
                    <Typography component={"p"}>
                        <b>Step 3:</b>&nbsp;Discuss next steps to maximize your benefits.
                    </Typography>
                    <Box>
                        <ContactUsButton sx={{ ml: 0, width: { xs: "100%", lg: "unset" } }} />
                    </Box>
                    <Typography component={"p"}>
                        Ready to submit a MHD Pilot Application?{" "}
                        <LinkButton
                            href={CON_EDISON_MHD_APPLICATION_URL}
                            button={{
                                sx: {
                                    textTransform: "none",
                                    fontSize: "inherit",
                                    textDecoration: "underline",
                                    verticalAlign: "unset",
                                },
                            }}
                        >
                            Apply Now
                        </LinkButton>
                    </Typography>
                </Stack>
            );
        } else {
            return (
                <Stack direction={"column"} gap={2} sx={{ border: "1px solid", borderColor: "secondary.dark", p: 3 }}>
                    <Typography component={"p"}>
                        <b>How We Can Help:</b>
                    </Typography>
                    <Typography component={"p"} variant="body2">
                        <b>1. Schedule a Call With Our Advisory Team.</b>
                    </Typography>
                    <Typography component={"p"}>
                        Get personalized guidance on your project&apos;s eligibility.
                    </Typography>
                    <Typography component={"p"} variant="body2">
                        <b>2. Learn About Voucher Incentive Programs.</b>
                    </Typography>
                    <Typography component={"p"}>
                        Explore{" "}
                        <LinkButton
                            href={ROUTES.Vouchers}
                            button={{
                                sx: {
                                    display: "inline",
                                    textTransform: "none",
                                    fontSize: "inherit",
                                    textDecoration: "underline",
                                    verticalAlign: "baseline",
                                },
                            }}
                        >
                            voucher incentive programs
                        </LinkButton>{" "}
                        to maximize your project&apos;s incentive eligibility.
                    </Typography>
                    <Box>
                        <ContactUsButton sx={{ ml: 0, width: { xs: "100%", lg: "unset" } }} />
                    </Box>
                </Stack>
            );
        }
    }, [dacPercent, projectType, publicPercent]);

    const body = useMemo(() => {
        if (projectType !== EProjectTypes.MHD) return null;

        return (
            <Stack direction={"column"} gap={1} sx={{ maxWidth: "936px" }}>
                <Typography component={"div"} fontWeight={700}>
                    {subheader}
                </Typography>
                {isMHD && (
                    <List>
                        {bullets.map((b, i: number) => (
                            <Fragment key={`bullet-${i}`}>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{ justifyContent: "center" }}>
                                        {dacPercent || publicPercent ? <Check sx={{ color: "success.main" }} /> : "•"}
                                    </ListItemIcon>
                                    <ListItemText>{b}</ListItemText>
                                </ListItem>
                                {!dacPercent && !publicPercent && i !== bullets.length - 1 && <ListItem>OR</ListItem>}
                            </Fragment>
                        ))}
                    </List>
                )}
                {nextSteps}
            </Stack>
        );
    }, [bullets, dacPercent, isMHD, nextSteps, projectType, publicPercent, subheader]);

    return () => ({
        h2,
        body,
    });
};
